import clsx from 'clsx';
import { ComponentPropsWithoutRef, FC, useEffect, useState } from 'react';
import { CheckboxIconProps } from '../../../assets';
import { Icon } from '../Icon';

export interface CheckboxProps
  extends Pick<ComponentPropsWithoutRef<'button'>, 'className' | 'disabled' | 'aria-label'>,
    Pick<CheckboxIconProps, 'type'> {
  defaultChecked?: boolean;
  inputName?: string;
  label?: string;
  onChange?: (checked: boolean) => void;
  iconTheme?: 'primary';
  partiallyChecked?: boolean;
}

export const Checkbox: FC<CheckboxProps> = ({
  defaultChecked,
  inputName,
  label,
  className,
  onChange,
  disabled,
  type,
  partiallyChecked = false,
  'aria-label': ariaLabel,
}) => {
  const [checked, setChecked] = useState(defaultChecked ?? false);

  useEffect(() => {
    if (defaultChecked != undefined) setChecked(defaultChecked);
  }, [defaultChecked]);

  return (
    <button
      disabled={disabled}
      type="button"
      role="checkbox"
      aria-checked={partiallyChecked ? 'mixed' : checked}
      aria-label={ariaLabel}
      className={clsx(
        'grid cursor-pointer grid-flow-col items-end justify-start gap-2',
        disabled && 'cursor-not-allowed opacity-40',
        className
      )}
      onClick={() => {
        if (disabled) return;
        setChecked(!checked);
        onChange?.(!checked);
      }}
    >
      {inputName && <input type="hidden" value={String(checked)} name={inputName} />}
      {type === 'square' && !partiallyChecked && (
        <Icon
          icon={checked ? 'checkboxSquareChecked' : 'checkboxSquareUnchecked'}
          className={clsx(checked ? 'text-palette-600' : 'text-darkMode-gray-medium')}
        />
      )}
      {type === 'square' && partiallyChecked && (
        <Icon icon="checkboxSquarePartiallyChecked" className={`text-darkMode-gray-medium`} />
      )}
      {type === 'circle' && (
        <Icon
          icon={checked ? 'checkboxCircleChecked' : 'checkboxCircleUnchecked'}
          className={clsx(checked ? 'text-palette-600' : 'text-darkMode-gray-medium')}
        />
      )}
      {label && <p className="text-lg font-light leading-normal tracking-[0.28px] text-darkMode-gray-light">{label}</p>}
    </button>
  );
};
